import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { Edit as EditIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Sort, Inject, RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import { AppealLetterView, AppealStatus } from '../../../services/AppealsService';

interface AppealsGridProps {
  appeals: AppealLetterView[];
  onAppealSelect?: (appeal: AppealLetterView) => void;
}

const STATUS_COLORS: Record<AppealStatus, 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'> = {
  Draft: 'default',
  Generated: 'info',
  Reviewed: 'primary',
  Approved: 'success',
  Sent: 'secondary',
  Pending: 'warning',
  Won: 'success',
  Lost: 'error',
  Withdrawn: 'default',
};

const TYPE_LABELS: Record<string, string> = {
  FreeForm: 'Free Form',
  MedicalNecessity: 'Medical Necessity',
  PriorAuthDenial: 'Prior Auth Denial',
  CodingDispute: 'Coding Dispute',
  TimelyCoverage: 'Timely/Coverage',
  BillingError: 'Billing Error',
  OutOfNetwork: 'Out of Network',
};

const LEVEL_LABELS: Record<string, string> = {
  FirstLevel: '1st Level',
  SecondLevel: '2nd Level',
  ExternalReview: 'External',
  Reconsideration: 'Reconsider',
  PeerToPeer: 'Peer-to-Peer',
};

interface GridRowData {
  id: string;
  status: AppealStatus;
  templateType: string;
  templateTypeDisplay: string;
  appealType: string;
  appealTypeDisplay: string;
  patientName: string;
  payerName: string;
  claimNumber: string;
  createdAt?: string;
  createdAtDisplay: string;
  modifiedAt?: string;
  modifiedAtDisplay: string;
  pdfBlobUrl?: string;
  appeal: AppealLetterView;
}

const AppealsGrid: React.FC<AppealsGridProps> = ({ appeals, onAppealSelect }) => {
  const navigate = useNavigate();

  // Transform data for the grid
  const gridData: GridRowData[] = useMemo(() => 
    appeals.map(appeal => ({
      id: appeal.id,
      status: appeal.status,
      templateType: appeal.templateType,
      templateTypeDisplay: TYPE_LABELS[appeal.templateType] || appeal.templateType,
      appealType: appeal.appealType,
      appealTypeDisplay: LEVEL_LABELS[appeal.appealType] || appeal.appealType,
      patientName: appeal.claimContext?.patientName || 'N/A',
      payerName: appeal.claimContext?.payerName || 'N/A',
      claimNumber: appeal.claimContext?.claimNumber || 'N/A',
      createdAt: appeal.createdAt,
      createdAtDisplay: appeal.createdAt ? new Date(appeal.createdAt).toLocaleDateString() : '',
      modifiedAt: appeal.modifiedAt,
      modifiedAtDisplay: appeal.modifiedAt ? new Date(appeal.modifiedAt).toLocaleDateString() : '',
      pdfBlobUrl: appeal.pdfBlobUrl,
      appeal,
    }))
  , [appeals]);

  const statusTemplate = (props: GridRowData) => (
    <Chip 
      label={props.status}
      color={STATUS_COLORS[props.status]}
      size="small"
    />
  );

  const actionsTemplate = (props: GridRowData) => (
    <Box>
      <Tooltip title="Edit">
        <IconButton 
          size="small" 
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/appeals/edit/${props.id}`);
          }}
          sx={{ color: '#3b82f6' }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {props.pdfBlobUrl && (
        <Tooltip title="View PDF">
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              window.open(props.pdfBlobUrl, '_blank');
            }}
            sx={{ color: '#64748b' }}
          >
            <PdfIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const handleRowSelected = (args: RowSelectEventArgs) => {
    if (onAppealSelect && args.data) {
      onAppealSelect((args.data as GridRowData).appeal);
    }
  };

  if (appeals.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="textSecondary">
          No appeals found for this encounter. Click "New Appeal" to create one.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <GridComponent 
        dataSource={gridData}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 10, pageSizes: [10, 25, 50] }}
        sortSettings={{ columns: [{ field: 'modifiedAt', direction: 'Descending' }] }}
        rowSelected={handleRowSelected}
      >
        <ColumnsDirective>
          <ColumnDirective field="status" headerText="Status" width="120" template={statusTemplate} />
          <ColumnDirective field="templateTypeDisplay" headerText="Type" width="150" />
          <ColumnDirective field="appealTypeDisplay" headerText="Level" width="130" />
          <ColumnDirective field="patientName" headerText="Patient" width="180" />
          <ColumnDirective field="payerName" headerText="Payer" width="150" />
          <ColumnDirective field="claimNumber" headerText="Claim #" width="130" />
          <ColumnDirective field="createdAtDisplay" headerText="Created" width="120" />
          <ColumnDirective field="modifiedAtDisplay" headerText="Modified" width="120" />
          <ColumnDirective field="id" headerText="Actions" width="120" template={actionsTemplate} allowSorting={false} />
        </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>
    </Box>
  );
};

export default AppealsGrid;
